import { editorTextDefaults } from "@proof/authoring/editor-text";
import { loadIntegrationPreset } from "./integration-presets";
import { applyIntegrationStyle } from "./integration-style";
export function integrationMarkup(
  element: HTMLElement | undefined,
  id: string,
) {
  const tag = element?.localName || "proof-form";
  const escape = (value: string) =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll('"', "&quot;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  const attributes = [
    "mode",
    "modal",
    "show-proof",
    "width",
    "height",
    "max-width",
    "max-height",
    "dialog-title",
    "trigger-label",
  ]
    .flatMap((key) =>
      element?.hasAttribute(key)
        ? [` ${key}="${escape(element.getAttribute(key) || "")}"`]
        : [],
    )
    .join("");
  return `<${tag} id="${id}"${attributes}></${tag}>`;
}
export function integrationModuleUrl(element?: { localName: string }) {
  const moduleName = (element?.localName || "proof-form")
    .replace(/^proof-/, "")
    .replace("reusable-form", "reusable-forms");
  const resource = performance.getEntriesByType("resource").find((entry) => {
    try {
      return new URL(entry.name).pathname.endsWith(
        `/plugins/${moduleName}.mjs`,
      );
    } catch {
      return false;
    }
  });
  const loader = Array.from(document.scripts).find((script) => {
    try {
      return /\/(proof-form|embed)\.(m?js)$/.test(new URL(script.src).pathname);
    } catch {
      return false;
    }
  });
  const candidate = resource
    ? new URL(resource.name)
    : loader
      ? new URL(`plugins/${moduleName}.mjs`, loader.src)
      : undefined;
  if (candidate) {
    candidate.username = "";
    candidate.password = "";
    candidate.search = "";
    candidate.hash = "";
    return candidate.href;
  }
  return `/plugins/${moduleName}.mjs`;
}
/** Developer-only overlay. The host still owns permissions and persistence. */
export function integrationDesignMode(
  element: HTMLElement & { options?: any },
) {
  const shadow = element.shadowRoot;
  if (!shadow) return;
  // Every integration applies its theme and style preset (default or gated).
  applyIntegrationStyle(element, element.options || {});
  const enabled =
    element.hasAttribute("design-mode") || element.options?.designMode === true;
  let header = shadow.querySelector<HTMLElement>("[data-integration-design]");
  const mount = shadow.querySelector<HTMLElement>("div");
  if (!enabled) {
    header?.remove();
    shadow.querySelector("style[data-design-overlay]")?.remove();
    mount?.removeAttribute("data-design-mode");
    return;
  }
  // Identify configurable regions while designing.
  mount?.setAttribute("data-design-mode", "");
  if (!shadow.querySelector("style[data-design-overlay]")) {
    const overlay = document.createElement("style");
    overlay.dataset.designOverlay = "";
    overlay.textContent =
      "[data-design-mode] .panel,[data-design-mode] .section-tabs,[data-design-mode] .form-group,[data-design-mode] .integration-editor-footer,[data-design-mode] .field-list-filters,[data-design-mode] .page-intro{outline:1px dashed #2563eb;outline-offset:2px}" +
      "[data-integration-design]>.design-badge{display:inline-block;margin-right:6px;padding:2px 8px;border-radius:999px;background:#2563eb;color:#fff;font:600 11px system-ui}";
    shadow.append(overlay);
  }
  if (header) return;
  header = document.createElement("header");
  header.dataset.integrationDesign = "";
  header.style.cssText = "position:absolute;right:8px;top:8px;z-index:20";
  const badge = document.createElement("span");
  badge.className = "design-badge";
  badge.textContent = "Design mode";
  const gear = document.createElement("button");
  gear.type = "button";
  gear.textContent = "⚙";
  gear.setAttribute("aria-label", "Configure integration");
  const dialog = document.createElement("dialog");
  dialog.style.cssText =
    "width:min(700px,90vw);max-height:85dvh;margin:auto;padding:24px;overflow:auto";
  const title = document.createElement("h2");
  title.textContent = "Integration design mode";
  dialog.append(title);
  const options = () => element.options || {};
  if (
    [
      "proof-authoring",
      "proof-form-configuration",
      "proof-esm-project",
      "proof-reusable-form",
    ].includes(element.localName)
  )
    for (const [key, label] of [
      ["allowProjectRename", "Allow project name editing"],
      ["enableStatements", "Enable statements"],
      ["enableRules", "Enable calculated rules and presets"],
      ["enableFormatting", "Enable formatting"],
      ["enableValidation", "Enable validation"],
      ["enableDynamicSources", "Enable dynamic data sources"],
      ["enableConditionalVisibility", "Enable conditional visibility"],
      ["enablePartOverrides", "Enable part overrides"],
      ["enableFormDisplay", "Show the Form display section"],
      ["enableAdvancedSetup", "Enable Advanced setup"],
      ["enableJsonConfiguration", "Enable JSON configuration"],
      ["enableFieldFilters", "Show System / Calculated / Required filters"],
      ["enableSourceInputs", "Offer Use Source Input on sources"],
      ["enableCalculatedValues", "Show the Calculated value section"],
      ["enableFormLayout", "Show the Form layout tab"],
      ["enableFormGeneration", "Offer Generate Form"],
      ["enableFormPreview", "Offer View Form"],
      ["enableGlobalForms", "Offer global form generation"],
      ["enableDataPresets", "Show the Data / Rules & presets tab"],
      ["showHeading", "Show editor heading"],
      ["showDescription", "Show editor description"],
    ]) {
      const row = document.createElement("label");
      row.style.cssText = "display:block;padding:8px";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked =
        options().editor?.[key] ??
        options().services?.editorPresentation?.[key] ??
        // New optional capabilities default to off.
        (key.startsWith("enable") && key !== "enableSourceInputs");
      input.onchange = () => {
        const current = options();
        element.options =
          element.localName === "proof-authoring"
            ? {
                ...current,
                editor: { ...current.editor, [key]: input.checked },
              }
            : {
                ...current,
                services: {
                  ...current.services,
                  editorPresentation: {
                    ...current.services?.editorPresentation,
                    [key]: input.checked,
                  },
                },
              };
      };
      row.append(input, document.createTextNode(label));
      dialog.append(row);
    }
  if (
    [
      "proof-authoring",
      "proof-form-configuration",
      "proof-esm-project",
      "proof-reusable-form",
    ].includes(element.localName)
  ) {
    const group = document.createElement("details");
    const summary = document.createElement("summary");
    summary.textContent = "Text overrides";
    group.append(summary);
    const currentText = (): Record<string, string> =>
      options().editor?.text ??
      options().services?.editorPresentation?.text ??
      {};
    for (const [key, fallback] of Object.entries(editorTextDefaults)) {
      const row = document.createElement("label");
      row.style.cssText = "display:grid;gap:4px;padding:6px 8px";
      row.append(document.createTextNode(key));
      const input = document.createElement("input");
      input.type = "text";
      input.placeholder = fallback;
      input.value = currentText()[key] ?? "";
      input.onchange = () => {
        const current = options();
        const text = { ...currentText() };
        if (input.value.trim()) text[key] = input.value;
        else delete text[key];
        element.options =
          element.localName === "proof-authoring"
            ? { ...current, editor: { ...current.editor, text } }
            : {
                ...current,
                services: {
                  ...current.services,
                  editorPresentation: {
                    ...current.services?.editorPresentation,
                    text,
                  },
                },
              };
      };
      row.append(input);
      group.append(row);
    }
    {
      // Any other visible wording: one "Original = Replacement" per line.
      const row = document.createElement("label");
      row.style.cssText = "display:grid;gap:4px;padding:6px 8px";
      row.append(document.createTextNode("Label replacements"));
      const area = document.createElement("textarea");
      area.rows = 4;
      area.placeholder = "Add field = New input";
      const currentLabels = (): Record<string, string> =>
        options().editor?.labels ??
        options().services?.editorPresentation?.labels ??
        {};
      area.value = Object.entries(currentLabels())
        .map(([from, to]) => `${from} = ${to}`)
        .join("\n");
      area.onchange = () => {
        const labels = parseLabelReplacements(area.value);
        const current = options();
        element.options =
          element.localName === "proof-authoring"
            ? { ...current, editor: { ...current.editor, labels } }
            : {
                ...current,
                services: {
                  ...current.services,
                  editorPresentation: {
                    ...current.services?.editorPresentation,
                    labels,
                  },
                },
              };
      };
      row.append(area);
      group.append(row);
    }
    dialog.append(group);
  }
  {
    // Style presets: Propago styling is offered only when its ESM-gated
    // plugin loads for this account (Propago or Cormier domains).
    const row = document.createElement("label");
    row.style.cssText = "display:block;padding:8px";
    row.append(document.createTextNode("Integration style "));
    const select = document.createElement("select");
    select.setAttribute("aria-label", "Integration style");
    const add = (value: string, label: string) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      select.append(option);
    };
    add("default", "Default");
    select.value = options().themePreset || "default";
    void loadIntegrationPreset("propago", (module) =>
      integrationModuleUrl({ localName: `proof-${module}` }),
    ).then((available) => {
      if (!available) return;
      add("propago", "Propago");
      select.value = options().themePreset || "default";
    });
    select.onchange = () => {
      element.options = { ...options(), themePreset: select.value };
    };
    row.append(select);
    dialog.append(row);
  }
  for (const [key, value] of Object.entries(options()).filter(
    ([key, value]) =>
      key !== "themePreset" &&
      ["string", "boolean", "number"].includes(typeof value) &&
      !/password|token|secret|credential|headers|authorization|cookie|key|url/i.test(
        key,
      ),
  )) {
    const label = document.createElement("label");
    label.style.cssText = "display:block;padding:8px";
    label.append(document.createTextNode(key.replace(/([A-Z])/g, " $1") + " "));
    const input = document.createElement("input");
    input.type =
      typeof value === "boolean"
        ? "checkbox"
        : typeof value === "number"
          ? "number"
          : "text";
    if (typeof value === "boolean") input.checked = value;
    else input.value = String(value);
    input.onchange = () => {
      element.options = {
        ...options(),
        [key]:
          typeof value === "boolean"
            ? input.checked
            : typeof value === "number"
              ? Number(input.value)
              : input.value,
      };
    };
    label.append(input);
    dialog.append(label);
  }
  const button = (label: string, action: () => void) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.onclick = action;
    dialog.append(b);
  };
  button("Reload preview", () => {
    element.options = options();
  });
  button("Generate source", () => {
    const source = document.createElement("dialog");
    source.style.cssText =
      "width:min(800px,90vw);max-height:85dvh;margin:auto;padding:24px";
    const text = document.createElement("textarea");
    text.readOnly = true;
    text.rows = 16;
    text.style.cssText = "width:100%;box-sizing:border-box";
    text.setAttribute("aria-label", "Generated integration source");
    const current = options();
    const config = {
      ...Object.fromEntries(
        Object.entries(current).filter(
          ([key, value]) =>
            ["string", "boolean", "number"].includes(typeof value) &&
            !/password|token|secret|credential|headers|authorization|cookie|key|url/i.test(
              key,
            ),
        ),
      ),
      ...(current.editor ? { editor: current.editor } : {}),
      ...(current.theme ? { theme: current.theme } : {}),
      ...(current.permissions ? { permissions: current.permissions } : {}),
      ...(current.services?.editorPresentation
        ? {
            services: {
              editorPresentation: current.services.editorPresentation,
            },
          }
        : {}),
    };
    text.value = `${integrationMarkup(element, "integration")}\n<script type="module">\nimport ${JSON.stringify(integrationModuleUrl(element))};\ndocument.getElementById("integration").options = ${JSON.stringify(config, null, 2).replaceAll("<", "\\u003c")};\n${hostHooks(element, current)}</script>`;
    const close = document.createElement("button");
    close.textContent = "Close source";
    close.onclick = () => source.close();
    source.addEventListener("close", () => source.remove(), { once: true });
    source.append(text, close);
    shadow.append(source);
    source.showModal();
  });
  button("Close configuration", () => dialog.close());
  gear.onclick = () => dialog.showModal();
  header.append(badge, gear, dialog);
  shadow.append(header);
}
/** Host-side hooks implied by the configuration, as commented starter code. */
function hostHooks(element: HTMLElement, options: Record<string, any>) {
  const lines = [
    "// Supply authenticated data and host service callbacks separately.",
  ];
  const editor = [
    "proof-authoring",
    "proof-form-configuration",
    "proof-esm-project",
    "proof-reusable-form",
  ].includes(element.localName);
  if (options.sourceInputs)
    lines.push(
      'const integration = document.getElementById("integration");',
      'integration.addEventListener("proof:source-request", (event) => {',
      "  const { request, signal, respondWith } = event.detail;",
      "  respondWith(",
      "    loadMySource(request.key, request.kind, signal).then((data) => ({",
      "      data,",
      "      // translate: (data) => ({ choices: data.map((row) => ({ value: row.id, label: row.name })) }),",
      "    })),",
      "  );",
      "});",
    );
  if (editor)
    lines.push(
      "// Optional editor callbacks (services):",
      "//   listFormProfiles(query, signal) -> [{ id, name, values }]",
      "//   listPartOverrides(query, signal), searchPartSkus(query, signal) -> [{ id, name, sku }]",
      "//   loadPartOverride(id, signal) -> Part",
    );
  return lines.join(String.fromCharCode(10)) + String.fromCharCode(10);
}

/** Parse "Original = Replacement" lines; blank and malformed lines are ignored. */
export function parseLabelReplacements(source: string): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const line of source.split(/\r?\n/)) {
    const at = line.indexOf("=");
    if (at < 1) continue;
    const from = line.slice(0, at).trim(),
      to = line.slice(at + 1).trim();
    if (from && to) labels[from] = to;
  }
  return labels;
}
